import React from 'react';
import { BarChart3Icon, BookOpenIcon, CircleDollarSignIcon, FileTextIcon, GraduationCapIcon, LandmarkIcon } from 'lucide-react';
import { discoverImages } from '../../../data/discoverVisual';
import { useDiscover, type DiscoverSection } from '../../../contexts/DiscoverContext';
import { cn } from '../../../utils/cn';

/**
 * The Design B hero: a personalised workspace opener rather than an editorial
 * statement. It says who you are, what this surface is for, and gives the four
 * places to start — then hands straight over to the results.
 */
const ACTIONS: {
  section: DiscoverSection;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  tone: string;
}[] = [{
  section: 'Universities',
  title: 'Universities',
  description: 'Explore institutions',
  icon: LandmarkIcon,
  tone: 'bg-brand-50 text-brand-700'
}, {
  section: 'Programmes',
  title: 'Programmes',
  description: 'Find what to study',
  icon: BookOpenIcon,
  tone: 'bg-info-50 text-info-700'
}, {
  section: 'Scholarships',
  title: 'Scholarships',
  description: 'Find funding',
  icon: CircleDollarSignIcon,
  tone: 'bg-warn-50 text-warn-800'
}, {
  section: 'Applications',
  title: 'Applications',
  description: 'Track progress',
  icon: FileTextIcon,
  tone: 'bg-brand-50 text-brand-700'
}];
function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
export function HeroB() {
  const {
    section,
    setSection,
    universities,
    matchFor,
    studentField
  } = useDiscover();
  const top = universities[0];
  const match = top ? matchFor(top) : null;
  return <section className="relative overflow-hidden rounded-panel border border-line bg-surface px-5 py-7 sm:px-7" data-id="element-1397" data-mp="1iekjmlezx1pg">
      <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]" data-id="element-1398" data-mp="3hn948xe6050f">
        <div className="min-w-0" data-id="element-1399" data-mp="3cofqd3uyubzq">
          <p className="text-body text-ink-muted" data-id="element-1400" data-mp="3vulx91yf0q0s">
            {greeting()}, <span className="font-semibold text-brand-700" data-id="element-1401" data-mp="3vt13hejxdb3z">Amarachi</span>
          </p>
          <h1 className="mt-1.5 text-h1 font-bold tracking-tight text-ink" data-id="element-1402" data-mp="4hrbd16mbcf9">
            Discover your next step
          </h1>
          <p className="mt-2 max-w-[56ch] text-body-lg text-ink-muted" data-id="element-1403" data-mp="3rtjw2fkxu3w6">
            Find the university, programme or opportunity that fits what you are studying.
          </p>

          <ul className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1404" data-mp="jbmcfl0j7xfw">
            {ACTIONS.map(action => {
            const Icon = action.icon;
            const active = section === action.section;
            return <li key={action.section} data-id="element-1405" data-mp="eedw7pyy23ha">
                  <button type="button" onClick={() => setSection(action.section)} aria-current={active ? 'page' : undefined} className={cn('flex w-full items-center gap-3 rounded-card border bg-surface px-3.5 py-3 text-left transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(15,23,42,0.07)]', active ? 'border-brand-700 bg-brand-50/50' : 'border-line hover:border-brand-200')} data-id="element-1406" data-mp="ed2oxg6aidlf">
                    <span aria-hidden className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', action.tone)} data-id="element-1407" data-mp="2vkumqd8qx00y">
                      <Icon className="h-5 w-5" data-id="element-1408" data-mp="2hnxzelru60ot" />
                    </span>
                    <span className="min-w-0" data-id="element-1409" data-mp="2cw7j7z67wiph">
                      <span className="block text-body-sm font-semibold text-ink" data-id="element-1410" data-mp="2cv4pzklfa46e">
                        {action.title}
                      </span>
                      <span className="block truncate text-caption text-ink-muted" data-id="element-1411" data-mp="1k8k2iv82ic84">
                        {action.description}
                      </span>
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </div>

        {/* Personalisation, stated rather than implied. */}
        <div className="relative mx-auto hidden h-56 w-56 shrink-0 lg:block" data-id="element-1412" data-mp="27rdnx87m10au">
          <span aria-hidden className="absolute inset-0 rounded-full bg-brand-50 ring-1 ring-brand-100" data-id="element-1413" data-mp="2ck6tfyawkcnz" />
          <img src={discoverImages.hero} alt="A university student checking programme deadlines on her phone" className="absolute inset-2 h-[calc(100%-1rem)] w-[calc(100%-1rem)] rounded-full object-cover object-[35%_center]" data-id="element-1414" data-mp="2lk3e7ou68rdr" />
          <span aria-hidden className="absolute -right-1 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-brand-700 text-white ring-4 ring-surface" data-id="element-1415" data-mp="2vhht3k0g583y">
            <GraduationCapIcon className="h-5 w-5" data-id="element-1416" data-mp="114i81bdarlpa" />
          </span>

          {match && <div className="absolute -left-8 top-8 rounded-xl border border-line bg-surface px-3 py-2 shadow-[0_10px_26px_rgba(15,23,42,0.12)]" data-id="element-1417" data-mp="3eft3krhf8x95">
              <p className="cz-num text-body font-bold text-brand-700" data-id="element-1418" data-mp="35ly685mjkb42">{match.score}% match</p>
              <p className="text-caption text-ink-muted" data-id="element-1419" data-mp="30nd9ig6i09kd">for what you study</p>
            </div>}

          {studentField && <div className="absolute -right-6 bottom-6 flex items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2 shadow-[0_10px_26px_rgba(15,23,42,0.12)]" data-id="element-1420" data-mp="16oqd4bzsldw">
              <BarChart3Icon aria-hidden className="h-4 w-4 text-brand-700" data-id="element-1421" data-mp="3oc64a9bdwbde" />
              <span data-id="element-1422" data-mp="3jdl3a7ob5nnw">
                <span className="block text-body-sm font-semibold text-ink" data-id="element-1423" data-mp="3jee46s0l5iy4">{studentField}</span>
                <span className="block text-caption text-ink-muted" data-id="element-1424" data-mp="2cy25mptupz20">Top match</span>
              </span>
            </div>}
        </div>
      </div>

      <p className="mt-5 max-w-[76ch] text-caption text-ink-subtle" data-id="element-1425" data-mp="280iqzot8rk9j">
        Match is derived from your programme and level and from what each institution has published.
        It is not a prediction of whether you would be admitted, and it is not supplied by the
        institution. You can switch personalisation off in Settings.
      </p>
    </section>;
}